import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Tooltip } from '@/ui/primitives';
import { TABLE_TOOLTIP_ENTER_DELAY_MS } from '../tableTooltip';
import { elementHasOverflow } from './overflowTooltipUtils';
import type { SxProps, Theme } from '@/ui/primitives';
import type { TooltipProps } from '@/ui/primitives';
import type { ReactElement, ReactNode } from 'react';

type OverflowTooltipProps = {
  title: string | null | undefined;
  children: ReactNode;
  wrapperSx?: SxProps<Theme>;
  tooltipSx?: SxProps<Theme>;
  inline?: boolean;
  placement?: TooltipProps['placement'];
};

export const OverflowTooltip = ({
  title,
  children,
  wrapperSx,
  tooltipSx,
  inline = false,
  placement = 'top',
}: OverflowTooltipProps): ReactElement => {
  const containerRef = useRef<HTMLSpanElement | null>(null);
  const openTimerRef = useRef<number | null>(null);
  const [overflowed, setOverflowed] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const resolvedTitle = title?.trim() ?? '';

  const getOverflowState = useCallback((): boolean => {
    const node = containerRef.current;
    if (!node) return false;
    const childNode = node.firstElementChild;
    const childElement = childNode instanceof HTMLElement ? childNode : null;
    return Boolean(elementHasOverflow(node) || (childElement && elementHasOverflow(childElement)));
  }, []);

  const updateOverflowState = useCallback((): boolean => {
    const nextOverflowed = getOverflowState();
    setOverflowed(nextOverflowed);
    return nextOverflowed;
  }, [getOverflowState]);

  const clearOpenTimer = useCallback(() => {
    if (openTimerRef.current === null) return;
    window.clearTimeout(openTimerRef.current);
    openTimerRef.current = null;
  }, []);

  const handleOpen = useCallback(() => {
    clearOpenTimer();
    if (!resolvedTitle || !updateOverflowState()) {
      setTooltipOpen(false);
      return;
    }

    openTimerRef.current = window.setTimeout(() => {
      setTooltipOpen(true);
      openTimerRef.current = null;
    }, TABLE_TOOLTIP_ENTER_DELAY_MS);
  }, [clearOpenTimer, resolvedTitle, updateOverflowState]);

  const handleClose = useCallback(() => {
    clearOpenTimer();
    setTooltipOpen(false);
  }, [clearOpenTimer]);

  useEffect(() => {
    updateOverflowState();
    const node = containerRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      updateOverflowState();
    });
    observer.observe(node);
    const childNode = node.firstElementChild;
    if (childNode instanceof HTMLElement) {
      observer.observe(childNode);
    }
    return () => {
      observer.disconnect();
    };
  }, [resolvedTitle, updateOverflowState]);

  useEffect(() => {
    return () => {
      clearOpenTimer();
    };
  }, [clearOpenTimer]);

  useEffect(() => {
    if (!resolvedTitle || !overflowed) {
      setTooltipOpen(false);
    }
  }, [overflowed, resolvedTitle]);

  return (
    <Tooltip
      open={tooltipOpen}
      title={resolvedTitle}
      placement={placement}
      enterDelay={TABLE_TOOLTIP_ENTER_DELAY_MS}
      enterNextDelay={TABLE_TOOLTIP_ENTER_DELAY_MS}
      arrow
      slotProps={{
        tooltip: {
          sx: [
            {
              fontSize: '15px',
              px: '12px',
              py: '8px',
              whiteSpace: 'nowrap',
              width: 'fit-content',
              maxWidth: 'calc(100vw - 32px)',
            },
            ...(tooltipSx ? (Array.isArray(tooltipSx) ? tooltipSx : [tooltipSx]) : []),
          ],
        },
      }}
    >
      <Box
        ref={containerRef}
        component="span"
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
        onFocus={handleOpen}
        onBlur={handleClose}
        sx={[
          {
            minWidth: 0,
            maxWidth: '100%',
            width: inline ? 'auto' : '100%',
            display: inline ? 'inline-block' : 'block',
          },
          ...(wrapperSx ? (Array.isArray(wrapperSx) ? wrapperSx : [wrapperSx]) : []),
        ]}
      >
        {children}
      </Box>
    </Tooltip>
  );
};
